import { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'

interface CodeExample {
  language: string
  label: string
  code: string
}

interface CodeBlockProps {
  examples: CodeExample[]
  title?: string
}

export default function CodeBlock({ examples, title }: CodeBlockProps) {
  const [activeTab, setActiveTab] = useState(0)
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(examples[activeTab].code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'grey.200',
        my: 3,
      }}
    >
      <Box
        sx={{
          bgcolor: 'grey.800',
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {title && (
            <Typography
              variant="body2"
              sx={{ color: 'grey.400', fontWeight: 500, fontSize: '0.8rem' }}
            >
              {title}
            </Typography>
          )}
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              minHeight: 32,
              '& .MuiTabs-indicator': {
                bgcolor: 'primary.light',
              },
              '& .MuiTab-root': {
                minHeight: 32,
                py: 0.5,
                px: 1.5,
                fontSize: '0.75rem',
                fontWeight: 500,
                color: 'grey.400',
                textTransform: 'none',
                '&.Mui-selected': {
                  color: 'white',
                },
              },
            }}
          >
            {examples.map((example, index) => (
              <Tab key={index} label={example.label} />
            ))}
          </Tabs>
        </Box>
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{ color: 'grey.400', '&:hover': { color: 'white' } }}
        >
          {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Box
        component="pre"
        sx={{
          bgcolor: 'grey.900',
          color: 'grey.100',
          p: 2,
          m: 0,
          overflow: 'auto',
          fontSize: '0.85rem',
          fontFamily: '"JetBrains Mono", monospace',
          lineHeight: 1.6,
          maxHeight: 400,
        }}
      >
        <code>{examples[activeTab].code}</code>
      </Box>
    </Box>
  )
}

// Simple single-language code block
interface SimpleCodeBlockProps {
  code: string
  language?: string
}

export function SimpleCodeBlock({ code }: SimpleCodeBlockProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'grey.200',
        my: 2,
      }}
    >
      <IconButton
        size="small"
        onClick={handleCopy}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: 'grey.400',
          '&:hover': { color: 'white', bgcolor: 'grey.700' },
        }}
      >
        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
      <Box
        component="pre"
        sx={{
          bgcolor: 'grey.900',
          color: 'grey.100',
          p: 2,
          m: 0,
          overflow: 'auto',
          fontSize: '0.85rem',
          fontFamily: '"JetBrains Mono", monospace',
          lineHeight: 1.6,
        }}
      >
        <code>{code}</code>
      </Box>
    </Box>
  )
}

// Inline code component
export function InlineCode({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="code"
      sx={{
        bgcolor: 'grey.100',
        color: 'grey.800',
        px: 0.75,
        py: 0.25,
        borderRadius: 0.5,
        fontSize: '0.875em',
        fontFamily: '"JetBrains Mono", monospace',
      }}
    >
      {children}
    </Box>
  )
}
